import IconChevron from '$partials/icons/icon-chevron';
import IconClose from '$partials/icons/icon-close';
import { SimpleImage } from '$partials/media';
import { Button } from '$partials/ui';
import { formatLinkBlockToUrl } from '$utilities/data';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import React, { useEffect, useRef, useState } from 'react';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').OfficeTeamBlock}
 */

const OfficeTeamBlock = (props) => {
    return (
        <div className='module-office-team bg-main-background'>
            <div className='layout-container layout-site-outer-padding pt-24 pb-32 sm:py-40 md:py-48'>
                <div>
                    {props.heading && (
                        <h2 className='type-style-2 mb-16 sm:mb-32'>
                            {props.heading}
                        </h2>
                    )}
                </div>
                <div className='sm:-mx-16 md:-mx-24'>
                    {props.members?.length > 0 && (
                        <section className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-y-40'>
                            {props.members.map((member, i) => {
                                const agent = member?.agent;
                                if (!agent) { return null; }

                                return (
                                    <div
                                        className={`sm:px-16 md:px-24 sm:border-l sm:border-l-tint-dark ${i % 4 === 0 ? 'md:border-opacity-0' : 'md:border-opacity-20'} ${i % 2 === 0 ? 'sm:border-opacity-0' : 'sm:border-opacity-20'}`}
                                        key={member.id}>
                                        <MemberItem {...member?.agent}/>
                                    </div>
                                );
                            })}
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').OfficeTeamMemberItem}
 */
const MemberItem = (props) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Map Agent fields to component expectations
    const name = `${props?.firstName || ''} ${props?.lastName || ''}`.trim();
    const phoneNumber = props?.phoneDirect || props?.phoneMobile;
    const body = props?.about;

    return (
        <article className=''>
            <div className='aspect-square bg-grey-grey-300'>
                {props?.profileImage && (
                    <SimpleImage
                        {...props.profileImage}
                        className='w-full h-full object-cover'/>
                )}
            </div>

            <div className=''>
                <div className=''>
                    <h3 className='type-style-3 sm:type-style-4 mb-8 mt-24'>
                        {name}
                    </h3>

                    {props?.position && (
                        <p className='type-style-eyebrow mb-8'>
                            {props.position}
                        </p>
                    )}
                    {!props?.position && (
                        <div className='h-18'/>
                    )}
                </div>
                {/* Links */}
                <div className='flex flex-col mt-24'>
                    {body && (
                        <div className='py-16 border-t border-main-border'>
                            <button
                                className='type-style-eyebrow'
                                onClick={() => setIsDialogOpen(true)}
                                type='button'>
                                {'About'}
                            </button>
                        </div>
                    )}
                    {/* Phone */}
                    {phoneNumber && (
                        <div className='py-16 border-t border-main-border'>
                            <a
                                className='type-style-eyebrow'
                                href={`tel:${phoneNumber.replace(/[^\+\d]/g, '')}`}>
                                {phoneNumber}
                            </a>
                        </div>
                    )}
                    {/* Whatsapp */}
                    {props?.whatsapp && (
                        <div className='py-16 border-t border-main-border'>
                            <a
                                className='type-style-eyebrow'
                                href={`https://wa.me/${props.whatsapp.replace(/[^\+\d]/g, '')}`}>
                                {'Whatsapp'}
                            </a>
                        </div>
                    )}
                    {/* Email address */}
                    {props?.emailAddress && (
                        <div className='py-16 border-t border-main-border'>
                            <a
                                className='type-style-eyebrow'
                                href={`mailto:${props.emailAddress}`}>
                                {'Email'}
                            </a>
                        </div>
                    )}
                </div>
                {isDialogOpen && (
                    <MemberItemModal
                        body={body}
                        closeDialog={() => setIsDialogOpen(false)}
                        isOpen={isDialogOpen}
                        name={name}
                        phoneNumber={phoneNumber}
                        {...props}/>
                )}
            </div>
        </article>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').OfficeTeamMemberItemModal}
 */
const MemberItemModal = ({
    isOpen,
    closeDialog,
    name,
    phoneNumber,
    body,
    ...member
}) => {
    const dialogRef = useRef(null);
    const scrollElementRef = useRef(null);
    const [animationOut, setAnimationOut] = useState(false);

    const handleClose = () => {
        setAnimationOut(true);
        setTimeout(() => {
            closeDialog();
            setAnimationOut(false);
        }, 300);
    };

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        const scrollElementNode = scrollElementRef.current;
        if (isOpen) {
            if (dialogNode && !dialogNode.open && scrollElementNode) {
                disableBodyScroll(scrollElementNode);
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open && scrollElementNode) {
                enableBodyScroll(scrollElementNode);
                setAnimationOut(true);
                setTimeout(() => {
                    dialogNode.close?.();
                    setAnimationOut(false);
                }, 300);
            }
        }

        // Handle Escape key press
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                event.preventDefault();
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            enableBodyScroll(scrollElementNode);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen]);

    const socialLinks = member?.socialLinks || [];
    const offices = member?.offices?.edges?.map(edge => edge.node) || [];

    return (
        <dialog
            className='fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity'
            onClick={handleClose}
            ref={dialogRef}>
            {/* Modal Panel */}
            <div
                className={`px-16 py-32 sm:p-32 text-charcoal theme-light fixed md:right-0 md:top-0 bottom-0 left-0 right-0 sm:left-auto sm:right-0 md:left-auto md:bottom-auto h-full md:w-1/2 w-full max-w-full md:max-w-[880px] bg-main-background shadow-xl overflow-y-auto team-dialog ${animationOut ? 'team-dialog__outro' : ''}`}
                onClick={(e) => e.stopPropagation()}
                ref={scrollElementRef}>
                <Button
                    className='absolute z-20 top-24 right-24 flex select-none h-[40px] w-[40px] md:h-[44px] md:w-[44px] rounded justify-center items-center button-secondary-filled'
                    onClick={handleClose}>
                    <IconClose />
                </Button>

                {/* Two Column Layout */}
                <div className='grid grid-cols-1 sm:max-md:max-w-[472px] md:grid-cols-[260px_minmax(auto,444px)] gap-32 md:gap-40'>
                    {/* Left Column - Agent Card */}
                    <div className='max-w-[95%] sm:max-w-none flex flex-col md:sticky md:top-0 md:self-start'>
                        {member?.profileImage && (
                            <SimpleImage
                                {...member.profileImage}
                                className='w-[166px] sm:w-[252px] object-cover md:w-full aspect-square mb-16' />
                        )}

                        <div className='block md:hidden'>
                            <h2 className='type-style-3 mb-8'>
                                {name}
                            </h2>

                            {member?.position && (
                                <p className='type-style-eyebrow uppercase mb-2 text-charcoal/60'>
                                    {member.position}
                                </p>
                            )}

                            {offices.length > 0 && (
                                <div className='flex flex-wrap gap-x-4'>
                                    {offices.map((office, index) => (
                                        <React.Fragment key={office?.id || index}>
                                            <Button
                                                as='a'
                                                className='type-style-eyebrow underline'
                                                href={office?.relativeUrl}>
                                                {`${office?.title}`}
                                            </Button>
                                            {index < offices.length - 1 && <span className='type-style-eyebrow'>,</span>}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Contact Links */}
                        <div className='type-style-eyebrow mt-32 md:mt-16'>
                            {member?.emailAddress && (
                                <div className='border-t border-main-border py-16'>
                                    <Button
                                        as='a'
                                        className='flex items-center justify-between w-full'
                                        href={`mailto:${member.emailAddress}`}>
                                        <span>EMAIL</span>
                                        <IconChevron className='inline-block ml-5.5 -rotate-90' />
                                    </Button>
                                </div>
                            )}
                            {phoneNumber && (
                                <div className='border-t border-main-border py-16'>
                                    <Button
                                        as='a'
                                        className='flex items-center justify-between w-full'
                                        href={`tel:${phoneNumber.replace(/[^\+\d]/g, '')}`}>
                                        <span>{phoneNumber}</span>
                                        <IconChevron className='inline-block ml-5.5 -rotate-90' />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Social Links */}
                        {socialLinks.length > 0 && (
                            <div className='flex gap-8 mt-16 sm:mt-8'>
                                {socialLinks.map((linkBlock, index) => (
                                    <Button
                                        as='a'
                                        className='button-secondary-filled rounded w-[44px] h-[44px] flex items-center justify-center'
                                        href={formatLinkBlockToUrl(linkBlock.link)}
                                        key={linkBlock.id || index}>
                                        {linkBlock.logo?.image && (
                                            <SimpleImage
                                                {...linkBlock.logo.image}
                                                className='w-[13px]'/>
                                        )}
                                    </Button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Right Column - Bio & Details */}
                    <div className='max-w-[95%] sm:max-w-noneflex flex-col'>
                        <div className='hidden md:block mb-40'>
                            <h2 className='type-style-3 mb-8'>
                                {name}
                            </h2>

                            {member?.position && (
                                <p className='type-style-eyebrow uppercase mb-2 text-charcoal/60'>
                                    {member.position}
                                </p>
                            )}

                            {offices.length > 0 && (
                                <div className='flex flex-wrap gap-x-4'>
                                    {offices.map((office, index) => (
                                        <React.Fragment key={office?.id || index}>
                                            <Button
                                                as='a'
                                                className='type-style-eyebrow underline'
                                                href={office?.relativeUrl}>
                                                {`${office?.title}`}
                                            </Button>
                                            {index < offices.length - 1 && <span className='type-style-eyebrow'>,</span>}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className='flex flex-col gap-24 mt-24 sm:mt-0'>
                            {body && (
                                <div
                                    className='rich-text enquire-modal'
                                    dangerouslySetInnerHTML={{ __html: body}}/>
                            )}

                            {member?.specialities && (
                                <div>
                                    <h3 className='type-style-eyebrow uppercase mb-8 text-charcoal/60'>SPECIALITIES</h3>
                                    <p className='type-style-body'>{member.specialities}</p>
                                </div>
                            )}

                            {member?.license && (
                                <div>
                                    <h3 className='type-style-eyebrow uppercase mb-8 text-charcoal/60'>LICENSE</h3>
                                    <p className='type-style-body'>{member.license}</p>
                                </div>
                            )}

                            {member?.languages && (
                                <div>
                                    <h3 className='type-style-eyebrow uppercase mb-8 text-charcoal/60'>LANGUAGES</h3>
                                    <p className='type-style-body'>{member.languages}</p>
                                </div>
                            )}

                            {member?.serviceArea && (
                                <div>
                                    <h3 className='type-style-eyebrow uppercase mb-8 text-charcoal/60'>SERVICE AREA</h3>
                                    <p className='type-style-body'>{member.serviceArea}</p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </dialog>
    );
};


/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default OfficeTeamBlock;
